/**
 * BannerEditorPanel — dedicated Banner editor (Phase 11.6.2). "Banner" is the Manual
 * Editor's business term for the `hero` section kind — the SiteSchema, registry key,
 * runtime, and AI generator all keep using `"hero"`; only this screen's user-facing text
 * is renamed (see `SECTION_KIND_LABEL.hero` in editorContext.ts, the single source for
 * that rename).
 *
 * Exposes exactly the controls the production Hero component
 * (`site-builder/components/hero/Hero.tsx`, a literal clone of
 * `__reference__/realDemo/landing/components/banner`) actually renders: heading,
 * subheading, background image, primary/secondary action, layout, and visibility. No
 * Eyebrow control — the reference info block renders only a decorative bar, never the
 * eyebrow text (see `shared/blocks.tsx` InfoBlock), so an Eyebrow field would edit nothing.
 */
import type { LayoutId } from '../../site-builder/schema'
import { builderRegistry } from '../previewRuntime'
import { useBuilderEditor, assetUrl } from './editorContext'
import { Fieldset, SegmentedControl, TextField, TextArea, ToggleField, ImageUploadField } from './controls'

// Display labels only — internal layout ids (cover/coverCard/split/media) are unchanged.
const LAYOUT_LABELS: Record<string, string> = {
  cover: 'Cover',
  coverCard: 'Cover Center',
  split: 'Card',
  media: 'Media',
}

const ACTION_LABELS = ['Primary action', 'Secondary action']

export function BannerEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const layouts = (builderRegistry.entry('hero')?.supportedLayouts ?? []) as readonly LayoutId[]
  const actions = section.content.actions ?? []
  const imageValue = assetUrl(e.schema, section.content.media?.[0]?.assetId) || undefined

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show banner"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Background image">
        <ImageUploadField
          label="Background image"
          value={imageValue}
          onChange={(src) => e.setSectionImage(sectionId, src)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Subheading" value={section.content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      {layouts.length > 1 ? (
        <Fieldset title="Layout">
          <SegmentedControl<LayoutId>
            label="Layout"
            value={section.layout}
            options={layouts.map((l) => ({ value: l, label: LAYOUT_LABELS[l] ?? l }))}
            onChange={(v) => e.setSectionLayout(sectionId, v)}
          />
        </Fieldset>
      ) : null}

      {actions.length > 0 ? (
        <Fieldset title="Actions">
          {actions.map((a, i) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                {ACTION_LABELS[i] ?? `Action ${i + 1}`}
              </span>
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}
    </div>
  )
}
